/**
 * Componente AppointmentDetail
 * Modal o vista detallada de una cita
 * @module components/appointments/AppointmentDetail
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  CalendarIcon,
  MapPinIcon,
  UserIcon,
  ClockIcon,
  DocumentTextIcon,
  XMarkIcon,
  LinkIcon,
  PhoneIcon,
  CalendarDaysIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import type { Appointment } from '@/types/appointment.types';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AddToCalendarButton } from './AddToCalendarButton';
import { AppointmentSummary } from './AppointmentSummary';
import {
  getModalityLabel,
  getModalityBadgeClasses,
  getModalityIcon,
} from '@/utils/appointmentHelpers';

interface AppointmentDetailProps {
  appointment: Appointment;
  onClose?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  /** Guardar solo el resumen (PATCH summary). Si no se pasa, la sección resumen no es editable. */
  onSaveSummary?: (summary: string) => Promise<void>;
  /** Ir al contacto para realizar llamada de seguimiento (requiere contact_id). */
  onFollowUpCall?: () => void;
  /** Abrir modal para programar una llamada de seguimiento y crear tarea (requiere contact_id). */
  onScheduleCall?: () => void;
  /** Si false, los botones de llamada se muestran deshabilitados con tooltip (cita sin contacto). */
  hasContact?: boolean;
}

export const AppointmentDetail: React.FC<AppointmentDetailProps> = ({
  appointment,
  onClose,
  onEdit,
  onDelete,
  onSaveSummary,
  onFollowUpCall,
  onScheduleCall,
  hasContact = true,
}) => {
  const appointmentDate = new Date(appointment.scheduled_at);
  const ModalityIcon = getModalityIcon(appointment.modality);

  return (
    <Card className="max-w-2xl">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-xl font-bold">Detalle de Cita</CardTitle>
        {onClose && (
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Modalidad */}
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getModalityBadgeClasses(
              appointment.modality
            )}`}
          >
            <ModalityIcon className="w-4 h-4" aria-hidden />
            {getModalityLabel(appointment.modality)}
          </span>
        </div>

        {/* Fecha y hora */}
        <div className="flex items-start gap-3">
          <CalendarIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-gray-500">Fecha y hora</p>
            <p className="text-base font-semibold text-gray-900">
              {format(appointmentDate, "EEEE, d 'de' MMMM 'de' yyyy", {
                locale: es,
              })}
            </p>
            <p className="text-sm text-gray-600">
              {format(appointmentDate, 'HH:mm', { locale: es })} ({appointment.timezone})
            </p>
          </div>
        </div>

        {/* Duración */}
        <div className="flex items-start gap-3">
          <ClockIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-gray-500">Duración</p>
            <p className="text-base text-gray-900">{appointment.duration} minutos</p>
          </div>
        </div>

        {/* Contacto */}
        {appointment.contact_name && (
          <div className="flex items-start gap-3">
            <UserIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-500">Contacto</p>
              <p className="text-base text-gray-900">{appointment.contact_name}</p>
            </div>
          </div>
        )}

        {/* Agente */}
        {appointment.agent_name && (
          <div className="flex items-start gap-3">
            <UserIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-500">Agente responsable</p>
              <p className="text-base text-gray-900">{appointment.agent_name}</p>
            </div>
          </div>
        )}

        {/* Delegación */}
        {appointment.delegation_name && (
          <div className="flex items-start gap-3">
            <MapPinIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-500">Delegación</p>
              <p className="text-base text-gray-900">{appointment.delegation_name}</p>
            </div>
          </div>
        )}

        {/* Ubicación o enlace videollamada */}
        {appointment.location && (
          <div className="flex items-start gap-3">
            <MapPinIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-500">Ubicación</p>
              <p className="text-base text-gray-900">{appointment.location}</p>
            </div>
          </div>
        )}
        {appointment.meeting_url && (
          <div className="flex items-start gap-3">
            <LinkIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-500">Enlace videollamada</p>
              <a
                href={appointment.meeting_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-base text-primary hover:underline break-all"
              >
                {appointment.meeting_url}
              </a>
            </div>
          </div>
        )}

        {/* Notas */}
        {appointment.notes && (
          <div className="flex items-start gap-3">
            <DocumentTextIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-500">Notas</p>
              <p className="text-base text-gray-900 whitespace-pre-wrap">
                {appointment.notes}
              </p>
            </div>
          </div>
        )}

        {/* Resumen de la cita */}
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">Resumen de la cita</p>
          {onSaveSummary ? (
            <AppointmentSummary
              appointmentId={appointment.id}
              summary={appointment.summary ?? null}
              status={appointment.status ?? null}
              onSave={onSaveSummary}
              showPendingAlert={true}
            />
          ) : (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
              {appointment.summary ? (
                <p className="whitespace-pre-wrap text-gray-900">{appointment.summary}</p>
              ) : (
                'Sin resumen.'
              )}
            </div>
          )}
        </div>

        {/* Metadatos */}
        <div className="pt-4 border-t border-gray-200 text-xs text-gray-500 space-y-1">
          <p>Creada: {format(new Date(appointment.created_at), 'Pp', { locale: es })}</p>
          <p>
            Actualizada: {format(new Date(appointment.updated_at), 'Pp', { locale: es })}
          </p>
          <p className="font-mono">ID: {appointment.id}</p>
        </div>

        {/* Acciones: dos filas para evitar superposición en móvil; flex-1 en móvil para buenos touch targets */}
        <div className="flex flex-col gap-3 pt-4 border-t border-gray-200">
          <div className="flex flex-wrap items-center gap-3">
            <AddToCalendarButton appointment={appointment} />
            {appointment.contact_id && (
              <Button
                asChild
                variant="outline"
                className="flex-1 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0"
              >
                <Link to={`/crm/contacts/${appointment.contact_id}`}>
                  <UserCircleIcon className="w-4 h-4 mr-2 shrink-0" aria-hidden />
                  Ver contacto
                </Link>
              </Button>
            )}
            {onFollowUpCall != null && (
              <Button
                onClick={onFollowUpCall}
                disabled={!hasContact}
                title={!hasContact ? 'La cita debe tener un contacto asociado' : undefined}
                className="flex-1 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0"
                variant="default"
              >
                <PhoneIcon className="w-4 h-4 mr-2 shrink-0" aria-hidden />
                Realizar llamada de seguimiento
              </Button>
            )}
            {onScheduleCall != null && (
              <Button
                onClick={onScheduleCall}
                disabled={!hasContact}
                title={!hasContact ? 'La cita debe tener un contacto asociado' : undefined}
                variant="outline"
                className="flex-1 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0"
              >
                <CalendarDaysIcon className="w-4 h-4 mr-2 shrink-0" aria-hidden />
                Programar llamada de seguimiento
              </Button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {onEdit && (
              <Button onClick={onEdit} className="flex-1 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 bg-blue-600 hover:bg-blue-700">
                Editar cita
              </Button>
            )}
            {onDelete && (
              <Button
                onClick={onDelete}
                variant="outline"
                className="flex-1 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 text-red-600 border-red-600 hover:bg-red-50"
              >
                Eliminar cita
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
